// Stop across midnight (architecture sprint-04 §6.1 F44). Cancel / Esc sends nothing: the timer keeps
// running and the version is unchanged (decision_required wrote nothing).
import type { StopResult } from '@/types/timer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDurationHm, formatShortLocalDate } from '@/lib/format';

type DecisionRequired = Extract<StopResult, { outcome: 'decision_required' }>;

interface MidnightDialogProps {
  decision: DecisionRequired | null;
  pending: boolean;
  onChoose: (midnight: 'split' | 'keep') => void;
  onCancel: () => void;
}

export function midnightBody(d: DecisionRequired): string {
  return `It ran from ${formatShortLocalDate(d.firstDate)} to ${formatShortLocalDate(d.lastDate)} and crossed midnight ${d.days - 1} time(s) (${formatDurationHm(d.workedSeconds)} in total).`;
}

export function MidnightDialog({ decision, pending, onChoose, onCancel }: MidnightDialogProps): JSX.Element {
  return (
    <Dialog
      open={decision !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      {/* The original's small confirm: side-by-side choices, then a full-width Cancel. */}
      <DialogContent className="max-w-sm" showClose={false}>
        <DialogHeader>
          <DialogTitle>Timer crossed midnight</DialogTitle>
          <DialogDescription>{decision ? midnightBody(decision) : ''}</DialogDescription>
        </DialogHeader>
        {decision && <p className="text-xs text-muted-foreground">Keep as one: saved on {formatShortLocalDate(decision.firstDate)}, capped at 24h.</p>}
        <div className="flex gap-2">
          <Button type="button" className="flex-1" onClick={() => onChoose('split')} disabled={pending}>
            {pending ? 'Saving…' : 'Split at midnight'}
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={() => onChoose('keep')} disabled={pending}>
            Keep as one
          </Button>
        </div>
        <Button type="button" variant="ghost" className="mt-0 w-full" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
}
